import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Settings2, Trash2, Plus, AlertTriangle, ChevronDown, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

export const ST_COLORS = {
  amber: "bg-amber-50 text-amber-700 border-amber-200", orange: "bg-orange-50 text-orange-700 border-orange-200",
  red: "bg-red-50 text-red-700 border-red-200", violet: "bg-violet-50 text-violet-700 border-violet-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200", sky: "bg-sky-50 text-sky-700 border-sky-200",
  slate: "bg-slate-100 text-slate-700 border-slate-300", zinc: "bg-zinc-200 text-zinc-800 border-zinc-300",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", blue: "bg-blue-50 text-blue-700 border-blue-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200", pink: "bg-pink-50 text-pink-700 border-pink-200",
};
const DOT = { amber: "bg-amber-500", orange: "bg-orange-500", red: "bg-red-500", violet: "bg-violet-500", rose: "bg-rose-500", sky: "bg-sky-500", slate: "bg-slate-500", zinc: "bg-zinc-600", emerald: "bg-emerald-500", blue: "bg-blue-500", teal: "bg-teal-500", pink: "bg-pink-500" };

// Shows manual status if set, otherwise the automatic badge (children). Clickable for editors.
export const VehicleStatusBadge = ({ asset, canEdit, onOpen, children, suffix = "" }) => {
  const op = asset.op_status;
  const inner = op
    ? <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ST_COLORS[op.color] || ST_COLORS.slate}`} title={op.note || ""}>
        <span className={`h-1.5 w-1.5 rounded-full ${DOT[op.color] || DOT.slate}`} />{op.label}</span>
    : children;
  if (!canEdit) return <span data-testid={`vehicle-status-${asset.id}${suffix}`}>{inner}</span>;
  return (
    <button type="button" onClick={() => onOpen(asset)} data-testid={`vehicle-status-${asset.id}${suffix}`} title="تغيير الحالة"
      className="group inline-flex items-center gap-0.5 rounded-full transition-transform hover:scale-105">
      {inner}<ChevronDown className="h-3 w-3 text-slate-400 group-hover:text-slate-700" />
    </button>
  );
};

export const VehicleStatusDialog = ({ asset, statuses, onClose, onSaved }) => {
  const [sel, setSel] = useState("auto");
  const [note, setNote] = useState("");
  const [day, setDay] = useState("");
  const [ret, setRet] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (asset) { setSel(asset._preset || asset.op_status?.key || "auto"); setNote(""); setDay(new Date().toISOString().slice(0, 10)); setRet(true); } }, [asset]);
  const st = statuses.find((s) => s.key === sel);
  const holder = asset?.current && asset.current.employee_id !== "__admin__" ? asset.current.employee_name : null;
  const save = async () => {
    setBusy(true);
    try { await api.setVehicleStatus(asset.id, { status: sel, note, date: day, return_current: ret }); toast.success("تم تحديث حالة المركبة"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التحديث"); } finally { setBusy(false); }
  };
  const hist = (asset?.op_status_history || []).slice(-5).reverse();
  return (
    <Dialog open={!!asset} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-md" data-testid="vehicle-status-dialog">
        <DialogHeader className="pe-6 text-right">
          <DialogTitle className="font-heading">حالة المركبة</DialogTitle>
          <DialogDescription dir="ltr" className="text-right">{asset?.identifier}</DialogDescription>
        </DialogHeader>
        <div className="grid max-h-64 grid-cols-2 gap-1.5 overflow-y-auto">
          <button type="button" onClick={() => setSel("auto")} data-testid="vehicle-status-opt-auto"
            className={`col-span-2 flex items-center gap-2 rounded-xl border px-3 py-2 text-right text-sm ${sel === "auto" ? "border-emerald-300 bg-emerald-50 font-bold text-emerald-800" : "border-slate-200 hover:bg-slate-50"}`}>
            <CheckCircle2 className="h-4 w-4" /> تلقائي (مُسندة / متاحة)</button>
          {statuses.map((s) => (
            <button key={s.key} type="button" onClick={() => setSel(s.key)} data-testid={`vehicle-status-opt-${s.key}`}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-right text-sm ${sel === s.key ? `${ST_COLORS[s.color]} font-bold` : "border-slate-200 hover:bg-slate-50"}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[s.color] || DOT.slate}`} />{s.label}</button>))}
        </div>
        {st?.blocking && holder && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="vehicle-status-holder-warning">
            <p className="flex items-center gap-1.5 font-bold"><AlertTriangle className="h-4 w-4" /> المركبة حالياً مع {holder}</p>
            <label className="mt-2 flex items-center gap-2"><input type="checkbox" checked={ret} onChange={(e) => setRet(e.target.checked)} className="accent-amber-600" data-testid="vehicle-status-return-check" /> استلامها منه تلقائياً الآن</label>
            <p className="mt-1 text-[11px] text-amber-700">لن يمكن تسليمها لموظف جديد حتى تعود الحالة "تلقائي".</p>
          </div>)}
        {sel !== "auto" && (
          <div className="space-y-2">
            <div><Label className="text-xs">التاريخ</Label><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 h-9 w-48" data-testid="vehicle-status-date" /></div>
            <div><Label className="text-xs">{sel === "workshop" ? "سبب دخول الورشة" : "ملاحظة (اختيارية)"}</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="vehicle-status-note" /></div>
          </div>)}
        {hist.length > 0 && (
          <div className="rounded-xl bg-slate-50 p-2.5 text-[11px] text-slate-600" data-testid="vehicle-status-history">
            <p className="mb-1 font-bold text-slate-700">آخر التغييرات</p>
            {hist.map((h, i) => <p key={i}>• {h.date} — <b>{h.label}</b> — {h.by}{h.returned_from ? ` (استُلمت من ${h.returned_from})` : ""}{h.note ? ` · ${h.note}` : ""}</p>)}
          </div>)}
        <DialogFooter><Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={save} disabled={busy} data-testid="vehicle-status-save">{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />} حفظ</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const VehicleStatusManager = ({ statuses, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("teal");
  const [blocking, setBlocking] = useState(true);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { onChanged(await fn()); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(false); } };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="vehicle-statuses-manage"><Settings2 className="h-4 w-4" /> حالات المركبات</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-md" data-testid="vehicle-statuses-dialog">
          <DialogHeader className="text-right"><DialogTitle className="font-heading">حالات المركبات</DialogTitle>
            <DialogDescription>الحالات الأساسية ثابتة، ويمكنك إضافة حالات خاصة بشركتك.</DialogDescription></DialogHeader>
          <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
            {statuses.map((s) => (
              <span key={s.key} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${ST_COLORS[s.color]}`}>
                {s.label}{!s.blocking && <span className="text-[10px] opacity-70">(لا تمنع التسليم)</span>}
                {s.custom && <button onClick={() => run(() => api.deleteVehicleStatus(s.key))} disabled={busy} data-testid={`vehicle-status-del-${s.key}`}><Trash2 className="h-3 w-3" /></button>}
              </span>))}
          </div>
          <div className="space-y-2 rounded-xl border border-slate-200 p-3">
            <Label className="text-xs">حالة جديدة</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="مثال: بانتظار قطع غيار" data-testid="vehicle-status-new-label" />
            <div className="flex flex-wrap gap-1.5">{Object.keys(DOT).map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} className={`h-6 w-6 rounded-full ${DOT[c]} ${color === c ? "ring-2 ring-offset-2 ring-slate-800" : ""}`} data-testid={`vehicle-status-color-${c}`} />))}</div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={blocking} onChange={(e) => setBlocking(e.target.checked)} data-testid="vehicle-status-new-blocking" /> تمنع تسليم المركبة لموظف</label>
            <Button size="sm" disabled={busy || !label.trim()} className="gap-1" data-testid="vehicle-status-add"
              onClick={() => run(async () => { const r = await api.addVehicleStatus({ label: label.trim(), color, blocking }); setLabel(""); return r; })}><Plus className="h-4 w-4" /> إضافة</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
